import { supabase } from '../../config/supabaseClient';
import { ENV } from '../../config/env';

export interface PmProjectTemplate {
  id: string;
  organization_id: string;
  name: string;
  description: string | null;
  template_data: Record<string, any>;
  is_public: boolean;
  created_by: string;
  created_at: string;
  updated_at: string;
  use_count: number;
}

async function getToken(): Promise<string> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not authenticated');
  return session.access_token;
}

const API_BASE_URL = `${ENV.API_URL}/api/project-management/templates`;

export const pmTemplatesApiService = {
  async list(organizationId: string): Promise<{ data: PmProjectTemplate[]; error: any }> {
    try {
      const token = await getToken();
      const queryParams = new URLSearchParams({ organization_id: organizationId });

      const response = await fetch(`${API_BASE_URL}?${queryParams.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();
      if (!response.ok) {
        return { data: [], error: result?.error || 'Failed to fetch templates' };
      }

      return { data: (result.data || []) as PmProjectTemplate[], error: null };
    } catch (e: any) {
      return { data: [], error: e?.message || 'Network error' };
    }
  },

  async create(input: {
    organization_id: string;
    name: string;
    description?: string | null;
    is_public?: boolean;
    template_data: Record<string, any>;
  }): Promise<{ data: PmProjectTemplate | null; error: any }> {
    try {
      const token = await getToken();

      const response = await fetch(API_BASE_URL, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(input),
      });

      const result = await response.json();
      if (!response.ok) {
        return { data: null, error: result?.error || 'Failed to create template' };
      }

      return { data: (result.data || null) as PmProjectTemplate | null, error: null };
    } catch (e: any) {
      return { data: null, error: e?.message || 'Network error' };
    }
  },

  async delete(templateId: string, organizationId: string): Promise<{ error: any }> {
    try {
      const token = await getToken();
      const queryParams = new URLSearchParams({ organization_id: organizationId });

      const response = await fetch(
        `${API_BASE_URL}/${encodeURIComponent(templateId)}?${queryParams.toString()}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      const result = await response.json();
      if (!response.ok) {
        return { error: result?.error || 'Failed to delete template' };
      }

      return { error: null };
    } catch (e: any) {
      return { error: e?.message || 'Network error' };
    }
  },

  async instantiate(
    templateId: string,
    input: {
      organization_id: string;
      project_name: string;
      project_description?: string | null;
      project_overrides?: Record<string, any>;
    }
  ): Promise<{ data: any; error: any }> {
    try {
      const token = await getToken();

      const response = await fetch(`${API_BASE_URL}/${encodeURIComponent(templateId)}/instantiate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(input),
      });

      const result = await response.json();
      if (!response.ok) {
        return { data: null, error: result?.error || 'Failed to create project from template' };
      }

      return { data: result.data, error: null };
    } catch (e: any) {
      return { data: null, error: e?.message || 'Network error' };
    }
  },
};

export default pmTemplatesApiService;
